import {inject, Injectable} from '@angular/core';
import {FieldValueService} from '../../core/field-value.service';
import {ChangeTrackerService} from '../../core/change-tracker.service';
import {ModelFactoryService} from '../../core/model-factory.service';
import {MetaField} from '../../core/definitions/meta-field';
import {BaseModel} from '../../core/definitions/base-model';
import {KeepObject} from '../../core/definitions/keep-object';
import {FieldParameters} from '../../core/definitions/field-parameters';
import {SuperObjectModel} from "../../core/definitions/super-object-model";
import {Section} from "../../core/definitions/sections-container";
import {CrudService} from "../../core/crud.service";

export class CopyKeepServiceParams {
  object: BaseModel;
  field: MetaField;
  index: number;
  fieldChangeCallback: any;

  constructor(object: BaseModel, field: MetaField, index: number, fieldChangeCallback?: any) {
    this.object = object;
    this.field = field;
    this.index = index;
    this.fieldChangeCallback = fieldChangeCallback;
  }
}

@Injectable({
  providedIn: 'root'
})
export class CopyKeepService {
  // Injections
  private readonly changeTracker: ChangeTrackerService = inject(ChangeTrackerService);
  private readonly crud: CrudService = inject(CrudService);
  private readonly fieldValueSvc: FieldValueService = inject(FieldValueService);
  private readonly modelFactory: ModelFactoryService = inject(ModelFactoryService);

  getKeepProps(params: CopyKeepServiceParams): KeepObject {
    let keepProps: KeepObject;
    if (params.object && params.object.$$keep) {
      keepProps = params.object.$$keep.fields[params.field.name];
      if (keepProps && params.index !== undefined) {
        keepProps = keepProps.indexes[params.index];
      }
    }
    return keepProps;
  }

  moveKeepObjects(fieldParameters: FieldParameters, previousIndex: number, currentIndex: number) {
    if (!fieldParameters.object.$$keep) {
      return;
    }
    const indexes = fieldParameters.object.$$keep.fields[fieldParameters.field.name].indexes;
    const moved = indexes[previousIndex];
    if (previousIndex > currentIndex) {
      // Item has been moved up
      for (let t = previousIndex ; t > currentIndex ; t--) {
        indexes[t] = indexes[t - 1];
      }
    } else {
      // Item has been moved down
      for (let t = previousIndex ; t < currentIndex ; t++) {
        indexes[t] = indexes[t + 1];
      }
    }
    indexes[currentIndex] = moved;
  }

  /**
   * The following conditions shall prevent the "keep" checkbox
   * from being displayed next to a field:
   *  - Copied fields missing values
   *  - Copied fields that are required
   *  - New array items, added after the copy was created
   *  - Fields within a newly created array item object
   *
   * @param keep boolean, whether field shall be default kept or not
   * @param params containing model information
   */
  setKeep(keep: boolean, params: CopyKeepServiceParams) {
    const res = this.checkCanKeep(params);
    if (res) {
      const keepProps = this.getSetKeepProps(params);
      if (keepProps) {
        keepProps.canKeep = keep;
      }
    }
    return res;
  }


  hasChanges(params: CopyKeepServiceParams) {
    return this.changeTracker.checkFieldValueChanged(params.object, params.field.name);
  }

  removeNotKeep(object: SuperObjectModel) {
    this.recursiveRemoveKeep(object);
  }

  setKeepSection(model: BaseModel, section: Section, keep: boolean) {
    this.setKeepSectionFields(model, section, keep);
  }

  private checkCanKeep(params: CopyKeepServiceParams) {
    let res = true;
    if (params.object) {
      if (params.index !== undefined) {
        const arrayItem: BaseModel = params.object[params.field.name][params.index];
        if (arrayItem !== undefined) {
          res = !this.crud.getCreate(arrayItem);
        } else {
          console.warn('No item ' + params.index + ' in ' + params.field.name);
        }
      }
      if (res) {
        res = this.fieldValueSvc.hasValue(params.field, params.object) &&
          !this.crud.getCreate(params.object) && !params.field.is_required;
      }
      if (res) {
        res = !this.hasChanges(params);
      }
    }
    return res;
  }

  private getSetKeepProps(params: CopyKeepServiceParams): KeepObject {
    let keepProps: KeepObject;
    if (params.object) {
      params.object.$$keep = params.object.$$keep || new KeepObject();
      params.object.$$keep.fields[params.field.name] =
        params.object.$$keep.fields[params.field.name] || new KeepObject();
      keepProps = params.object.$$keep.fields[params.field.name];
      if (params.index !== undefined) {
        keepProps.indexes[params.index] = keepProps.indexes[params.index] || new KeepObject();
        keepProps = keepProps.indexes[params.index];
      }
      if (params.fieldChangeCallback) {
        this.changeTracker.setFieldChangeCallback(params.object, params.field.name, params.fieldChangeCallback);
      }
    }
    return keepProps;
  }

  private recursiveRemoveKeep(obj: BaseModel) {
    let keepObject: KeepObject = null;
    if (obj) {
      keepObject = obj.$$keep;
    } else {
      console.warn('No object!');
    }
    if (!keepObject) {
      return;
    }
    for (const [fieldName, keepProps] of Object.entries(keepObject.fields)) {
      if (keepProps.canKeep === false) {
        obj[fieldName] = null;
      }
      if (!(keepProps.indexes && obj[fieldName])) {
        continue;
      }
      this.loopSortedIndexRemoveKeep(obj, fieldName, keepProps.indexes, this.getReverseSortedIndexes(keepProps.indexes));
    }
    this.loopRemoveKeepFromSubModels(obj)
  }

  private loopSortedIndexRemoveKeep(obj: BaseModel,
                                    fieldName: string,
                                    keepPropIndexes: {[name: number]: KeepObject},
                                    reversedIndex: string[]) {
    for (const indexStr of reversedIndex) {
      const index = Number.parseInt(indexStr, 10);
      // 'keep props' will not exist for new elements added after copy
      const keepPropsNdx = keepPropIndexes[index];
      if (keepPropsNdx) {
        if (keepPropsNdx.canKeep === false) {
          obj[fieldName].splice(index, 1);
        } else {
          this.recursiveRemoveKeep(obj[fieldName][index]);
        }
      }
    }
  }

  private loopRemoveKeepFromSubModels(obj: BaseModel) {
    for (const subObj of Object.values(obj)) {
      if (subObj && subObj.$$keep) {
        this.recursiveRemoveKeep(subObj);
      }
    }
  }

  private getReverseSortedIndexes(indexes: {[name: number]: KeepObject}): string[] {
    return Object.keys(indexes).sort((index1, index2) => {
      let res = 0;
      if (Number(index1) < Number(index2)) {
        res = 1;
      } else if (Number(index1) > Number(index2)) {
        res = -1;
      }
      return res;
    });
  }

  private setKeepSectionFields(model: BaseModel, section: Section, keep: boolean) {
    for (const secField of section.fields) {
      this.setKeepSectionField(keep, model, secField);
    }
  }

  private setKeepSectionField(keep: boolean, model: BaseModel, sectionField: MetaField) {
    let secModel = model;
    const path = sectionField.path;
    if (path) {
      for (const s of path.split('.')) {
        secModel = secModel[s];
      }
    }
    if (secModel) {
      this.recursiveSetKeep(keep, secModel, sectionField);
    } else {
      console.warn('Model not found for ' + sectionField.path);
    }
  }

  private recursiveSetKeep(keep: boolean, object: BaseModel, field: MetaField) {
    this.modelFactory.traverseModelField((model: BaseModel, fieldName: string, index: number) => {
      const fieldRes = model.$$meta[fieldName];
      this.setKeep(keep, new CopyKeepServiceParams(model, fieldRes, index));
    }, object, field.name);
  }

}
